import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { cn, getUserId } from "../lib/utils";

interface ChatRow {
  id: string;
  title: string;
  updatedAt: string;
}

interface Props {
  onNewChat: () => void;
  /** Bumped by parent when a chat finishes streaming so the list refreshes
   * without the user having to click "+ New" or reload. */
  refreshSignal?: number;
  /** Chat id currently streaming, if any. Its delete button is disabled and
   * deleteChat no-ops for it: deleting a chat mid-stream would race the turn's
   * persist + trace write (there is no cross-store tombstone). Required (not
   * optional) so ChatHome must wire the guard — typecheck proves it. */
  activeStreamingChatId: string | undefined;
}

export function ChatSidebar({ onNewChat, refreshSignal = 0, activeStreamingChatId }: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const [chats, setChats] = useState<ChatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const userId = getUserId();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch("/api/chats", { headers: { "x-user-id": userId } })
      .then((r) => r.json() as Promise<{ chats?: ChatRow[] }>)
      .then((j) => {
        if (cancelled) return;
        setChats(Array.isArray(j.chats) ? j.chats : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // params.id is intentional: navigating between chats in the sidebar
    // should refresh the list (in case the new chat has a generated title).
  }, [userId, reloadKey, refreshSignal, params.id]);

  const deleteChat = async (id: string) => {
    if (id === activeStreamingChatId) return; // race guard: don't delete a chat mid-stream
    if (!confirm("Delete this chat?")) return;
    try {
      await fetch(`/api/chats/${id}`, {
        method: "DELETE",
        headers: { "x-user-id": userId },
      });
      setChats((prev) => prev.filter((c) => c.id !== id));
      if (params.id === id) navigate("/", { replace: true });
    } catch (err) {
      console.error("delete failed:", err);
    }
  };

  return (
    <aside
      className="hidden md:flex w-64 shrink-0 flex-col border-r border-border bg-muted/30"
      data-testid="chat-sidebar"
    >
      <div className="flex items-center justify-between border-b border-border p-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          History
        </span>
        <button
          type="button"
          data-testid="new-chat-button"
          onClick={() => {
            onNewChat();
            navigate("/", { replace: false });
            setReloadKey((k) => k + 1);
          }}
          className="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
        >
          + New
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {loading && (
          <div className="px-2 py-1 text-xs text-muted-foreground">Loading…</div>
        )}
        {error && (
          <div className="px-2 py-1 text-xs text-destructive">{error}</div>
        )}
        {!loading && !error && chats.length === 0 && (
          <div className="px-2 py-4 text-xs text-muted-foreground">
            No chats yet.
          </div>
        )}
        {!loading && chats.length > 0 && (
          <ul className="space-y-0.5" data-testid="sidebar-chat-list">
            {chats.map((c) => (
              <li key={c.id} className="group flex items-center gap-1">
                <Link
                  to={`/chat/${c.id}`}
                  data-testid={`sidebar-chat-${c.id}`}
                  className={cn(
                    "flex-1 truncate rounded-md px-2 py-1.5 text-xs hover:bg-accent",
                    params.id === c.id && "bg-accent font-medium",
                  )}
                  title={c.title}
                >
                  {c.title}
                </Link>
                <button
                  type="button"
                  onClick={() => deleteChat(c.id)}
                  disabled={c.id === activeStreamingChatId}
                  data-testid={`sidebar-delete-${c.id}`}
                  className="invisible rounded px-1 text-xs text-muted-foreground group-hover:visible hover:text-destructive disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Delete chat"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
